import { Color } from "@repo/app-types/constants/colors";
import type { ProjectWithInterviewCount } from "@repo/app-types/entities/project";
import {
  buildProjectUrlPath,
  ProjectDetailsTabPath,
} from "@repo/app-types/helpers/adminAppRoutes";
import { Size } from "@repo/ui/helpers/helpers";
import { useInfiniteScroll } from "@repo/ui/helpers/useInfiniteScroll";
import { Pill } from "@repo/ui/Pills/pill";
import {
  TableSkeleton,
  TableSkeletonContent,
} from "@repo/ui/Skeletons/skeletons";
import { SmallTimeStamp } from "@repo/ui/SmallTimeStamp/smallTimeStamp";
import { EntityTableEmptyState } from "@repo/ui/tables/entityTableEmptyState";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import partition from "lodash-es/partition.js";
import type * as React from "react";
import { type FC, type ReactNode, useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { useAllProjectsForCompanyInfiniteQuery } from "../../api/entities/project.js";
import { useAppDispatch, useAppSelector } from "../../hooks/hook.js";
import { selectIsRecruitingModeCompany } from "../company/companySlice.js";
import { TeammateAvatarPile } from "../teammate/teammateAvatarPile.js";
import { selectTeammate } from "../teammate/teammateSlice.js";
import {
  fetchTeammates,
  selectAllTeammates,
} from "../teammates/teammatesSlice.js";

export type ProjectStatusFilter = "all" | "live" | "paused";

interface ProjectsTableProps {
  filterType?: "training";
  statusFilter?: ProjectStatusFilter;
}

export const ProjectsTable: React.FC<ProjectsTableProps> = ({
  filterType,
  statusFilter = "all",
}) => {
  const dispatch = useAppDispatch();

  // Fetch teammates for avatar display
  useEffect(() => {
    dispatch(fetchTeammates());
  }, [dispatch]);

  const navigate = useNavigate();
  const isRecruitingModeCompany = useAppSelector(selectIsRecruitingModeCompany);
  const teammate = useAppSelector((state) => selectTeammate(state));

  const teammates = useAppSelector((state) =>
    selectAllTeammates(state.teammates),
  );

  // Fetch projects with RTK Query
  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useAllProjectsForCompanyInfiniteQuery({
    statusFilter: statusFilter === "all" ? undefined : statusFilter,
    typeFilter: filterType,
    sort: "user",
  });

  // Flatten paginated data (already filtered server-side)
  const projects = useMemo(
    () => data?.pages.flatMap((page) => page.projects) ?? [],
    [data],
  );

  // Infinite scroll: observe when the user scrolls near the bottom
  const { loadMoreSentinelRef } = useInfiniteScroll({
    entities: projects,
    onLoadMore: hasNextPage && !isFetchingNextPage ? fetchNextPage : undefined,
  });

  const renderTableRows = () => {
    // Show a skeleton only if we're loading and there are no projects previously fetched
    if (isLoading && projects.length === 0) {
      return <TableSkeleton />;
    }

    if (isError) {
      return (
        <tr>
          <td colSpan={100}>
            <EntityTableEmptyState>
              Failed to load projects. Please try again.
            </EntityTableEmptyState>
          </td>
        </tr>
      );
    }

    if (!projects.length) {
      return (
        <tr>
          <td colSpan={100}>
            <EntityTableEmptyState>
              {filterType === "training"
                ? "No trainings created yet. Create one to get started."
                : "No projects created yet. Create one to get started."}
            </EntityTableEmptyState>
          </td>
        </tr>
      );
    }

    const [ownProjects, otherProjects] = partition(
      projects,
      (project) => teammate && project.owner_teammate_ids.includes(teammate.id),
    );

    const needsCategoryRows =
      ownProjects.length > 0 && otherProjects.length > 0;

    return (
      <>
        {needsCategoryRows
          ? renderTableRowCategory(
              filterType === "training"
                ? "My trainings"
                : isRecruitingModeCompany
                  ? "My job openings"
                  : "My projects",
            )
          : null}
        {ownProjects.map(renderTableRowProject)}
        {needsCategoryRows
          ? renderTableRowCategory(
              filterType === "training"
                ? "Other trainings"
                : isRecruitingModeCompany
                  ? "Other job openings"
                  : "Other projects",
            )
          : null}
        {otherProjects.map(renderTableRowProject)}
        {hasNextPage ? (
          <tr key="loading-sentinel">
            <td colSpan={100}>
              <div ref={loadMoreSentinelRef}>
                <TableSkeletonContent rowCount={1} />
              </div>
            </td>
          </tr>
        ) : null}
      </>
    );
  };

  const renderTableRowProject = (project: ProjectWithInterviewCount) => (
    <tr
      className="cursor-pointer hover:bg-blue-50"
      key={project.id}
      onClick={(event) => {
        // Support opening the project in a new tab when cmd/ctrl-clicking; otherwise navigate in-place
        const url =
          filterType === "training"
            ? buildProjectUrlPath(project.id, ProjectDetailsTabPath.Overview)
            : buildProjectUrlPath(project.id);
        if (event.metaKey || event.ctrlKey) {
          window.open(url, "_blank", "noopener,noreferrer");
          return;
        }
        navigate(url);
      }}
    >
      <td className="py-4 pr-3 pl-6 text-sm font-medium whitespace-nowrap text-foreground">
        <div className="flex items-center justify-between space-x-2">
          <div className="flex flex-row items-center space-x-2 overflow-hidden text-ellipsis">
            <div>
              {project.name}
              {project.interview_count ? (
                <span className="text-muted-foreground">
                  {` (${project.interview_count})`}
                </span>
              ) : null}
            </div>
            {!project.is_live ? (
              <Pill label="Paused" color={Color.Gray} size={Size.SMALL} />
            ) : null}
          </div>
          <TeammateAvatarPile
            className="-my-4"
            teammates={project.owner_teammate_ids.map((teammateId) =>
              teammates.find((teammate) => teammate.id === teammateId),
            )}
          />
        </div>
      </td>
      {isRecruitingModeCompany ? (
        filterType === "training" ? null : (
          <td className="px-3 py-4 text-sm text-muted-foreground">
            {project.job_type ? (
              <Pill
                label={
                  <TooltipTrigger>
                    <TooltipHoverTarget className="block max-w-[150px] truncate">
                      {project.job_type.name}
                    </TooltipHoverTarget>
                    <SimpleTooltip>{project.job_type.name}</SimpleTooltip>
                  </TooltipTrigger>
                }
                color={Color.Gray}
                size={Size.SMALL}
              />
            ) : null}
          </td>
        )
      ) : (
        <td className="px-3 py-4 text-sm whitespace-nowrap text-muted-foreground">
          {project.incentive_quantity
            ? `$${project.incentive_quantity}`
            : "None"}
        </td>
      )}
      <td className="px-3 py-4 text-sm whitespace-nowrap text-muted-foreground">
        <SmallTimeStamp
          timestamp={project.updated_at || project.created_at}
          className="text-sm text-muted-foreground"
        />
      </td>
    </tr>
  );

  const renderTableRowCategory = (category: ReactNode) => (
    <tr>
      <td
        className="px-6 pt-4 pb-2 text-xs font-semibold text-muted-foreground"
        colSpan={100}
      >
        {category}
      </td>
    </tr>
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="flow-root">
        <div className="-mx-4 -my-2 overflow-x-visible sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle">
            <div className="overflow-hidden rounded-lg shadow-sm ring-1 ring-black/5">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50 px-3">
                  <tr>
                    <ProjectTableHeaderCell className="min-w-[300px] pr-3 pl-6">
                      Name
                    </ProjectTableHeaderCell>
                    {isRecruitingModeCompany ? (
                      filterType === "training" ? null : (
                        <ProjectTableHeaderCell className="w-[180px] px-3">
                          Job type
                        </ProjectTableHeaderCell>
                      )
                    ) : (
                      <ProjectTableHeaderCell className="w-[100px] px-3">
                        Incentive
                      </ProjectTableHeaderCell>
                    )}
                    <ProjectTableHeaderCell className="w-[100px] pr-6 pl-3">
                      Updated
                    </ProjectTableHeaderCell>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-light bg-white">
                  {renderTableRows()}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProjectTableHeaderCell: FC<{ className: string; children: ReactNode }> = (
  props,
) => (
  <th
    scope="col"
    className={twMerge(
      "py-3 text-left text-xs font-semibold whitespace-nowrap text-foreground",
      props.className,
    )}
  >
    {props.children}
  </th>
);
